import * as React from "react";
import FormControl from "@mui/material/FormControl";
import {Checkbox, FormControlLabel, FormGroup, FormLabel} from "@mui/material";
import {useEffect} from "react";
import { useArrayState } from "../../../hooks/use_array_state";

interface ICheckboxOption<T> {
    label: string;
    value: T;
}

interface IMultipleCheckboxProps<T> {
    options: ICheckboxOption<T>[];
    value: T[];
    onChange(values: T[]): void;
}

interface ISingleCheckboxProps {
    value: boolean;
    onChange(checked: boolean): void;
    label: string;
}

interface IBaseCheckboxProps {
    label?: string;
}

type ICheckboxFieldProps<T> = (IMultipleCheckboxProps<T> | ISingleCheckboxProps) & IBaseCheckboxProps;
type ISingleCheckboxFieldProps = ISingleCheckboxProps & IBaseCheckboxProps;
type IMultipleCheckboxFieldProps<T> = IMultipleCheckboxProps<T> & IBaseCheckboxProps;

function isMultipleCheckboxProps<T>(props: ICheckboxFieldProps<T>): props is IMultipleCheckboxProps<T> & IBaseCheckboxProps {
    return (props as IMultipleCheckboxProps<T>).options !== undefined;
}

export const CheckboxField = <T,>(props: ICheckboxFieldProps<T>) => {
    if (isMultipleCheckboxProps(props)) {
        return <MultipleCheckboxField {...props} />
    }

    return <SingleCheckboxField {...props} />;
};

const SingleCheckboxField = ({label, value, onChange}: ISingleCheckboxFieldProps) => {
    // value === true it's because sometimes "value" happens not to be a boolean, this trick, fixes the problem.
    const [checked, setChecked] = React.useState<boolean>(value === true);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };

    useEffect(() => {
        onChange(checked);
    }, [checked]);

    return (
        <FormControl fullWidth>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={checked}
                        onChange={handleChange}
                        color="primary"
                    />
                }
                label={label}
            />
        </FormControl>
    );
};

const MultipleCheckboxField = <T,>({label, options, value, onChange}: IMultipleCheckboxFieldProps<T>) => {
    const {values, pushValue, deleteValue} = useArrayState<T>(value);

    const handleChange = (v: T) => () => {
        const index = values.indexOf(v);
        if (index === -1) {
            pushValue(v);
        } else {
            deleteValue(index)();
        }
    };

    const isChecked = (v: T): boolean => {
        return values.indexOf(v) > -1;
    };

    useEffect(() => {
        onChange(values);
    }, [values]);

    return (
        <FormControl fullWidth>
            {!!label && <FormLabel component="legend">{label}</FormLabel>}
            <FormGroup>
                {options.map((v, i) => (
                    <FormControlLabel key={i}
                        control={
                            <Checkbox
                                checked={isChecked(v.value)}
                                onChange={handleChange(v.value)}
                                color="primary"
                            />
                        }
                        label={v.label}
                    />
                ))}
            </FormGroup>
        </FormControl>
    );
}
